import * as React from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react/ArrowLeft';
import { ArrowRightIcon } from '@phosphor-icons/react/ArrowRight';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { cn } from '../../../utils/cn';
import { Button } from '../button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface CarouselProps {
  /**
   * Embla's options (`align`, `loop`, `dragFree`, ...); `axis` follows
   * `orientation` whatever is set here.
   */
  opts?: CarouselOptions;

  /**
   * `horizontal` by default; `vertical` stacks the slides and turns the arrows.
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * Embla plugins (autoplay, wheel gestures), passed through as they are.
   */
  plugins?: CarouselPlugin;

  /**
   * Hands the Embla api to the caller once it exists, for a counter or a
   * control outside the carousel.
   */
  setApi?: (api: CarouselApi) => void;
}

interface CarouselContextProps {
  /**
   * `undefined` until Embla has mounted on the viewport.
   */
  api: CarouselApi;

  /**
   * Whether a slide follows the current one; `false` until Embla has mounted.
   */
  canScrollNext: boolean;

  /**
   * Whether a slide comes before the current one; `false` until Embla has
   * mounted.
   */
  canScrollPrev: boolean;

  /**
   * The `opts` prop as given, without the `axis` taken from `orientation`.
   */
  opts?: CarouselOptions;

  /**
   * Resolved from the prop, never `undefined`.
   */
  orientation: 'horizontal' | 'vertical';

  /**
   * The ref `CarouselContent` puts on its viewport, where Embla mounts.
   */
  carouselRef: UseEmblaCarouselType[0];

  /**
   * Moves one slide on; a no-op until Embla has mounted.
   */
  scrollNext: () => void;

  /**
   * Moves one slide back; a no-op until Embla has mounted.
   */
  scrollPrev: () => void;

  /**
   * Listens for Embla's `select` and `reInit` and returns the unsubscribe: the
   * `subscribe` of a `useSyncExternalStore` reading the api's state.
   */
  subscribe: (onChange: () => void) => () => void;
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

/**
 * The carousel's api and state, for a part rendered inside a `Carousel`.
 */
function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) throw new Error('useCarousel must be used within a <Carousel />');

  return context;
}

function Carousel({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const axis = orientation === 'horizontal' ? 'x' : 'y';
  const [carouselRef, api] = useEmblaCarousel({ ...opts, axis }, plugins);

  const subscribe = React.useCallback(
    (onChange: () => void) => {
      api?.on('reInit', onChange);
      api?.on('select', onChange);

      return () => {
        api?.off('reInit', onChange);
        api?.off('select', onChange);
      };
    },
    [api],
  );
  const canScrollPrev = React.useSyncExternalStore(
    subscribe,
    () => api?.canScrollPrev() ?? false,
    () => false,
  );
  const canScrollNext = React.useSyncExternalStore(
    subscribe,
    () => api?.canScrollNext() ?? false,
    () => false,
  );

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  const contextValue = React.useMemo(
    () => ({
      api,
      canScrollNext,
      canScrollPrev,
      carouselRef,
      opts,
      orientation,
      scrollNext,
      scrollPrev,
      subscribe,
    }),
    [
      api,
      canScrollNext,
      canScrollPrev,
      carouselRef,
      opts,
      orientation,
      scrollNext,
      scrollPrev,
      subscribe,
    ],
  );

  const classes = {
    root: cn('relative', className),
  };

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        aria-roledescription="carousel"
        className={classes.root}
        data-slot="carousel"
        role="region"
        onKeyDownCapture={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef, orientation } = useCarousel();
  const isHorizontal = orientation === 'horizontal';

  const classes = {
    track: cn('flex', { '-ml-4': isHorizontal, '-mt-4 flex-col': !isHorizontal }, className),
  };

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={classes.track}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<'div'>) {
  const { orientation } = useCarousel();
  const isHorizontal = orientation === 'horizontal';

  const classes = {
    item: cn(
      'min-w-0 shrink-0 grow-0 basis-full',
      { 'pl-4': isHorizontal, 'pt-4': !isHorizontal },
      className,
    ),
  };

  return (
    <div
      aria-roledescription="slide"
      className={classes.item}
      data-slot="carousel-item"
      role="group"
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const isHorizontal = orientation === 'horizontal';

  const classes = {
    button: cn(
      'absolute size-8 rounded-full',
      {
        'top-1/2 -left-12 -translate-y-1/2': isHorizontal,
        '-top-12 left-1/2 -translate-x-1/2 rotate-90': !isHorizontal,
      },
      className,
    ),
  };

  return (
    <Button
      className={classes.button}
      data-slot="carousel-previous"
      disabled={!canScrollPrev}
      size={size}
      variant={variant}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeftIcon aria-hidden />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = 'outline',
  size = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const isHorizontal = orientation === 'horizontal';

  const classes = {
    button: cn(
      'absolute size-8 rounded-full',
      {
        'top-1/2 -right-12 -translate-y-1/2': isHorizontal,
        '-bottom-12 left-1/2 -translate-x-1/2 rotate-90': !isHorizontal,
      },
      className,
    ),
  };

  return (
    <Button
      className={classes.button}
      data-slot="carousel-next"
      disabled={!canScrollNext}
      size={size}
      variant={variant}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRightIcon aria-hidden />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

interface CarouselDotsProps {
  /**
   * Merged onto the row of dots.
   */
  className?: string;
}

/**
 * One dot per snap, the current one marked; a press scrolls to its slide. Reads
 * the carousel's api from context, so it renders inside a `Carousel`, and
 * renders nothing until there are two snaps to choose between.
 */
function CarouselDots({ className }: CarouselDotsProps) {
  const { api, subscribe } = useCarousel();
  const snapCount = React.useSyncExternalStore(
    subscribe,
    () => api?.scrollSnapList().length ?? 0,
    () => 0,
  );
  const selectedIndex = React.useSyncExternalStore(
    subscribe,
    () => api?.selectedScrollSnap() ?? 0,
    () => 0,
  );

  const handleDotClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      api?.scrollTo(Number(event.currentTarget.value));
    },
    [api],
  );

  const classes = {
    root: cn('flex items-center justify-center gap-1.5', className),
    dot: (isSelected: boolean) =>
      cn(
        'h-1.5 cursor-pointer rounded-full',
        'focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
        'motion-safe:transition-all motion-safe:duration-300',
        { 'bg-hairline-strong w-1.5': !isSelected, 'bg-fg-1 w-4': isSelected },
      ),
  };

  if (snapCount < 2) return null;

  const renderDot = (index: number) => {
    const isSelected = index === selectedIndex;
    const label = `Go to slide ${index + 1}`;

    return (
      <button
        key={index}
        aria-label={label}
        aria-selected={isSelected}
        className={classes.dot(isSelected)}
        data-slot="carousel-dot"
        role="tab"
        type="button"
        value={index}
        onClick={handleDotClick}
      />
    );
  };

  return (
    <div
      aria-label="Slides"
      className={classes.root}
      data-slot="carousel-dots"
      role="tablist"
    >
      {Array.from({ length: snapCount }, (_, index) => renderDot(index))}
    </div>
  );
}

export {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
};
